/**
 * 📭 EmptyState Component
 * Reusable empty state displays for different contexts
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  FileX2,
  Users,
  Store,
  BarChart3,
  Wallet,
  Search,
  Filter,
  type LucideIcon,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

type EmptyStateVariant = 'default' | 'table' | 'chart' | 'list' | 'search' | 'filter';

interface EmptyStateProps {
  variant?: EmptyStateVariant;
  icon?: LucideIcon;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'outline' | 'secondary';
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  compact?: boolean;
}

// ============================================
// VARIANT CONFIG
// ============================================

const getVariantDefaults = (variant: EmptyStateVariant) => {
  const variants: Record<EmptyStateVariant, { icon: LucideIcon; bgClass: string; iconClass: string }> = {
    default: {
      icon: FileX2,
      bgClass: 'bg-gray-50',
      iconClass: 'text-gray-400',
    },
    table: {
      icon: FileX2,
      bgClass: 'bg-gray-50/50',
      iconClass: 'text-gray-300',
    },
    chart: {
      icon: BarChart3,
      bgClass: 'bg-gray-50',
      iconClass: 'text-gray-300',
    },
    list: {
      icon: FileX2,
      bgClass: 'bg-gray-50',
      iconClass: 'text-gray-400',
    },
    search: {
      icon: Search,
      bgClass: 'bg-amber-50/50',
      iconClass: 'text-amber-400',
    },
    filter: {
      icon: Filter,
      bgClass: 'bg-blue-50/50',
      iconClass: 'text-blue-400',
    },
  };
  return variants[variant];
};

// ============================================
// MAIN COMPONENT
// ============================================

export const EmptyState: React.FC<EmptyStateProps> = ({
  variant = 'default',
  icon,
  title,
  description,
  action,
  secondaryAction,
  className,
  compact = false,
}) => {
  const defaults = getVariantDefaults(variant);
  const Icon = icon || defaults.icon;

  return (
    <div 
      className={cn(
        'flex flex-col items-center justify-center text-center',
        defaults.bgClass,
        compact ? 'py-8 px-4' : 'py-12 px-6',
        'rounded-lg',
        className
      )}
    >
      <div className={cn(
        'rounded-full flex items-center justify-center mb-4',
        compact ? 'w-12 h-12 bg-white/80' : 'w-16 h-16 bg-white shadow-sm'
      )}>
        <Icon className={cn(
          defaults.iconClass,
          compact ? 'w-6 h-6' : 'w-8 h-8'
        )} />
      </div>
      
      <h3 className={cn(
        'font-semibold text-gray-900',
        compact ? 'text-sm' : 'text-base'
      )}>
        {title}
      </h3>
      
      {description && (
        <p className={cn(
          'text-gray-500 mt-1 max-w-sm',
          compact ? 'text-xs' : 'text-sm'
        )}>
          {description}
        </p>
      )}
      
      {(action || secondaryAction) && (
        <div className={cn('flex gap-3', compact ? 'mt-4' : 'mt-6')}>
          {action && (
            <Button
              size={compact ? 'sm' : 'default'}
              variant={action.variant || 'default'}
              onClick={action.onClick}
              className={action.variant !== 'outline' ? 'bg-primary hover:bg-primary/90' : ''}
            >
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              size={compact ? 'sm' : 'default'}
              variant="ghost"
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// PRESET VARIANTS
// ============================================

export const EmptyStateNoLeads: React.FC<{ onAddLead?: () => void }> = ({ onAddLead }) => (
  <EmptyState
    icon={Users}
    title="Noch keine Leads"
    description="Erfasse deinen ersten Lead und starte die Akquise."
    action={onAddLead ? { label: 'Lead hinzufügen', onClick: onAddLead } : undefined}
  />
);

export const EmptyStateNoRestaurants: React.FC<{ onAddLead?: () => void }> = ({ onAddLead }) => (
  <EmptyState
    icon={Store}
    title="Noch keine Lokale"
    description="Starte jetzt mit der Akquise und verdiene Provisionen!"
    action={onAddLead ? { label: 'Ersten Lead erfassen', onClick: onAddLead } : undefined}
  />
);

export const EmptyStateNoCommissions: React.FC = () => (
  <EmptyState
    icon={Wallet}
    title="Keine Provisionen"
    description="Hier werden deine verdienten Provisionen angezeigt."
  />
);

export const EmptyStateNoPayouts: React.FC = () => (
  <EmptyState
    icon={Wallet}
    title="Keine Auszahlungen"
    description="Sobald du Provisionen sammelst, erscheinen hier deine Auszahlungen."
  />
);

export const EmptyStateNoChartData: React.FC = () => (
  <EmptyState
    variant="chart"
    title="Keine Daten verfügbar"
    description="Sobald Umsätze verzeichnet werden, siehst du hier deine Entwicklung."
    compact
  />
);

export const EmptyStateSearchNoResults: React.FC<{ query?: string; onClear?: () => void }> = ({ 
  query, 
  onClear 
}) => (
  <EmptyState
    variant="search"
    title="Keine Ergebnisse"
    description={query ? `Keine Treffer für "${query}"` : 'Versuche es mit anderen Suchbegriffen.'}
    action={onClear ? { label: 'Suche zurücksetzen', onClick: onClear, variant: 'outline' } : undefined}
    compact
  />
);

export const EmptyStateFilterNoResults: React.FC<{ onReset?: () => void }> = ({ onReset }) => (
  <EmptyState
    variant="filter"
    title="Keine Treffer"
    description="Die aktuellen Filter ergeben keine Ergebnisse."
    action={onReset ? { label: 'Filter zurücksetzen', onClick: onReset, variant: 'outline' } : undefined}
    compact
  />
);

// ============================================
// TABLE EMPTY STATE (inline variant)
// ============================================

export const EmptyStateTableRow: React.FC<{ 
  colSpan: number; 
  message?: string;
  icon?: LucideIcon;
}> = ({ 
  colSpan, 
  message = 'Keine Einträge vorhanden',
  icon: Icon = FileX2
}) => (
  <tr>
    <td colSpan={colSpan} className="py-12">
      <div className="flex flex-col items-center justify-center text-center">
        <Icon className="w-8 h-8 text-gray-300 mb-2" />
        <p className="text-sm text-gray-500">{message}</p>
      </div>
    </td>
  </tr>
);

export default EmptyState;
